// src/pages/HRAdmin/Department/DepartmentList.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';

const normalizeList = (payload) => {
  const list = payload?.data?.data ?? payload?.data ?? [];
  return Array.isArray(list) ? list : [];
};

const textOf = (value) => (value == null ? '' : String(value));

function StatCard({ icon, label, value, tone = 'accent' }) {
  const theme = useTheme();
  const toneColor =
    tone === 'success'
      ? theme.palette.success.main
      : tone === 'info'
        ? theme.palette.info.main
        : theme.palette.primary.main;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.25,
        height: '100%',
        borderRadius: '8px',
        border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
        background: theme.hr?.surface?.glass || alpha(theme.palette.background.paper, 0.88),
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '8px',
            display: 'grid',
            placeItems: 'center',
            color: toneColor,
            backgroundColor: alpha(toneColor, 0.11),
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {label}
          </Typography>
          <Typography variant="h6" sx={{ lineHeight: 1.15 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function DepartmentList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = () => {
    setLoading(true);
    setError('');
    api
      .get('/admin/departments')
      .then((res) => {
        setDepartments(normalizeList(res));
      })
      .catch((err) => {
        console.error('Failed to fetch departments:', err);
        setError('Could not load departments.');
      })
      .finally(() => setLoading(false));
  };

  const handleAddNew = () => {
    navigate('/admin/departments/new');
  };

  const handleEdit = (id) => {
    navigate(`/admin/departments/edit/${id}`);
  };

  const handleDelete = (id) => {
    if (!window.confirm('Are you sure you want to delete this department?')) return;
    api
      .delete(`/admin/departments/${id}`)
      .then(() => fetchDepartments())
      .catch((err) => {
        console.error('Failed to delete department:', err);
        setError('Could not delete this department.');
      });
  };

  const stats = useMemo(() => {
    const withManager = departments.filter((dept) => Boolean(dept.manager?.name)).length;
    const costCenters = new Set(
      departments.map((dept) => textOf(dept.cost_center).trim()).filter(Boolean)
    );

    return {
      total: departments.length,
      withManager,
      withoutManager: Math.max(departments.length - withManager, 0),
      costCenters: costCenters.size,
    };
  }, [departments]);

  const filteredDepartments = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return departments;

    return departments.filter((dept) => {
      const haystack = [
        dept.name,
        dept.code,
        dept.manager?.name,
        dept.manager?.employee_code,
        dept.cost_center,
      ]
        .map(textOf)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [departments, searchTerm]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        p: { xs: 1.25, md: 2 },
        background:
          theme.hr?.gradient?.page ||
          `linear-gradient(180deg, ${theme.palette.background.default}, ${alpha(
            theme.palette.primary.main,
            0.04
          )})`,
        '& .MuiButton-root, & .MuiOutlinedInput-root, & .MuiChip-root': {
          borderRadius: '8px',
        },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.25}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography variant="overline" color="text.secondary">
            Organization setup
          </Typography>
          <Typography variant="h4" sx={{ mt: -0.5 }}>
            Departments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Maintain department codes, managers, and cost center ownership.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-end', sm: 'initial' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchDepartments}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{ boxShadow: 'none' }}
          >
            Add Department
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<BusinessIcon fontSize="small" />} label="Departments" value={stats.total} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<ManageAccountsIcon fontSize="small" />}
            label="With manager"
            value={stats.withManager}
            tone="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<AccountTreeIcon fontSize="small" />}
            label="No manager"
            value={stats.withoutManager}
            tone="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<BusinessIcon fontSize="small" />}
            label="Cost centers"
            value={stats.costCenters}
            tone="info"
          />
        </Grid>
      </Grid>

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: '8px' }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          p: 1.25,
          mb: 1.5,
          borderRadius: '8px',
          border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
          background: theme.hr?.surface?.glass || alpha(theme.palette.background.paper, 0.88),
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
          <TextField
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search department, code, manager, or cost center"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Chip
            label={`${filteredDepartments.length} shown`}
            color="primary"
            variant="outlined"
            sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
          />
        </Stack>
      </Paper>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: '8px',
          border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
          background: theme.hr?.surface?.paper || theme.palette.background.paper,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 220 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Loading departments...
            </Typography>
          </Stack>
        ) : (
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Department</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Manager</TableCell>
                <TableCell>Cost Center</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDepartments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
                      <Typography variant="subtitle1">No departments found</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Try another search or add a new department.
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDepartments.map((dept) => (
                  <TableRow
                    key={dept.id}
                    hover
                    sx={{
                      '&:last-child td': { borderBottom: 0 },
                    }}
                  >
                    <TableCell>
                      <Typography variant="subtitle2">{dept.name || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      {dept.code ? <Chip size="small" label={dept.code} variant="outlined" /> : '-'}
                    </TableCell>
                    <TableCell>
                      {dept.manager?.name ? (
                        <Stack spacing={0.25}>
                          <Typography variant="body2">{dept.manager.name}</Typography>
                          {dept.manager?.employee_code ? (
                            <Typography variant="caption" color="text.secondary">
                              {dept.manager.employee_code}
                            </Typography>
                          ) : null}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Not assigned
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{dept.cost_center || '-'}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleEdit(dept.id)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => handleDelete(dept.id)}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
}

export default DepartmentList;
